import { Injectable } from '@angular/core';
import { catchError, combineLatest, map, Observable, of, shareReplay, startWith } from 'rxjs';
import { MeService } from '../api/me.service';
import { AuthService } from '@auth0/auth0-angular';
import { LandingModel } from '../models/laniding.model';
import { MeResponse } from '../api/api.types';

@Injectable({
  providedIn: 'root'
})
export class LandingService {
  constructor(
    private readonly meService: MeService,
    private readonly authService: AuthService
  ) { }

  getLandingActions(): Observable<LandingModel> {
    const isAuthenticated$ = this.authService.isAuthenticated$;

    // If /me fails (e.g., not logged in), treat as "no me" instead of erroring out
    const me$ = this.meService.getMe(true).pipe(
      catchError(() => of(null as MeResponse | null)),
      startWith(null as MeResponse | null)
    );

    // Ensure combineLatest can emit immediately
    const user$ = this.authService.user$.pipe(startWith(null));

    return combineLatest([isAuthenticated$, me$, user$]).pipe(
      map(([isAuthenticated, me, user]) => {

        // Not authenticated (or me is unavailable) => default VM
        if (!isAuthenticated || !me?.user) {

          console.log("No me or not authenticated");
          return {
            isAuthenticated: false,
            canAccessWorkspace: false,
            canRequestAccess: false,
            primaryLabel: 'Request Access',
            primaryDisabled: false,
            showAccessNote: false,
            // include this if your model has it
            // avatarPictureUri: null,
          } satisfies LandingModel;
        }

        const canAccessWorkspace =
          me.user.role === 'global_admin' || (me.user.role === 'gm' && me.user.isApproved);

        const isApprovedOrAdmin = !!canAccessWorkspace;
        const hasRequested = !!me.user.accessRequestedAt;
        const canRequestAccess = !isApprovedOrAdmin && !hasRequested;

        return {
          isAuthenticated: true,
          canAccessWorkspace,
          canRequestAccess,
          primaryLabel: canAccessWorkspace
            ? 'Go to Workspace'
            : canRequestAccess
              ? 'Request Access'
              : 'Pending Approval',
          primaryDisabled: !canAccessWorkspace && !canRequestAccess,
          showAccessNote: !canAccessWorkspace,
          // avatarPictureUri: (user as any)?.picture ?? null,
        } satisfies LandingModel;
      }),
      // Avoid refetching /me on every async pipe subscription
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
}